import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BOQItemPicker from "@/components/BOQItemPicker";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  UploadSimple,
  CheckCircle,
  XCircle,
  WarningCircle,
  FileText,
  FilePdf,
  Truck,
  Calendar,
  MapPin,
  Sparkle,
  Receipt,
  ShieldCheck,
  ShieldWarning,
  ThumbsUp,
  ThumbsDown,
  Note,
  PencilSimple,
  Trash,
  Eye,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

const STATUS_META = {
  open: { label: "Open", color: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  accepted: { label: "Accepted", color: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  partially_fulfilled: { label: "Partially Fulfilled", color: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  rejected: { label: "Rejected", color: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

const APPROVAL_META = {
  pending_approval: { label: "Pending Approval", color: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  pending_admin_review: { label: "Admin Review", color: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  approved: { label: "Approved", color: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  admin_approved: { label: "Admin Override", color: "bg-[#E0E7FF] text-[#3730A3] border-[#A5B4FC]" },
  rejected: { label: "Rejected", color: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

export default function PODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [po, setPO] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [fulfilment, setFulfilment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(null); // {invoice, action: 'approve'|'reject'}
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pos/${id}`);
      setPO(data);
      try {
        const [invList, ful] = await Promise.all([
          api.get(`/pos/${id}/invoices`),
          api.get(`/pos/${id}/fulfilment`),
        ]);
        setInvoices(invList.data || []);
        setFulfilment(ful.data || null);
      } catch {
        setInvoices([]);
        setFulfilment(null);
      }
    } catch (err) {
      toast.error("PO not found");
      navigate("/pos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pos/${id}/upload-invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Invoice processed: ${(data.po_status || data.ai_verdict || "processed").replace(/_/g, " ")}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/pos/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("PDF download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po?.po_number || "PO"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const openApprovalDialog = (invoice, action) => {
    setApprovalDialog({ invoice, action });
    setNotes("");
  };

  const submitApproval = async () => {
    if (!approvalDialog) return;
    const { invoice, action } = approvalDialog;
    if (action === "reject" && !notes.trim()) {
      toast.error("Notes are required when rejecting an invoice.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/invoices/${invoice.id}/${action}`, { notes });
      toast.success(action === "approve" ? "Invoice approved" : "Invoice rejected");
      setApprovalDialog(null);
      setNotes("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    const label = invoice.extracted_data?.invoice_number || invoice.original_filename || "this invoice";
    if (!window.confirm(`Delete "${label}"? This is permanent and the PO fulfilment will be recomputed.`)) return;
    try {
      await api.delete(`/invoices/${invoice.id}`);
      toast.success("Invoice deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const [reverifying, setReverifying] = useState(null); // invoice id currently being re-verified

  const handleReverifyInvoice = async (invoice) => {
    if (reverifying) return;
    const label = invoice.extracted_data?.invoice_number || invoice.original_filename || "this invoice";
    setReverifying(invoice.id);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/re-verify`);
      const verdict = data?.ai_verdict === "rejected" ? "Auto-rejected" : "Auto-accepted";
      toast.success(`${label} re-verified — ${verdict}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setReverifying(null);
    }
  };

  const handleViewInvoice = (invoice) => {
    const path = invoice.storage_path;
    if (!path) {
      toast.error("Invoice file is missing from storage");
      return;
    }
    window.open(`${API}/files/${path}`, "_blank", "noopener,noreferrer");
  };

  if (loading || !po) {
    return (
      <div className="p-10 text-center text-neutral-400">Loading PO…</div>
    );
  }

  const meta = STATUS_META[po.status] || STATUS_META.open;
  const isContract = po.po_type === "contract";
  const backTo = isContract ? "/contracts" : "/pos";
  const backLabel = isContract ? "Back to Contract POs" : "Back to Purchase Orders";

  return (
    <div className="p-8 sm:p-10 max-w-[1400px] mx-auto" data-testid="po-detail-page">
      {/* Back */}
      <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-primary mb-6">
        <ArrowLeft size={14} /> {backLabel}
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="font-mono-num text-2xl font-bold text-primary" data-testid="po-number">{po.po_number}</div>
            <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-0.5 border ${meta.color}`} data-testid="po-status-badge">
              {meta.label}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{po.vendor_name}</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {po.category}{po.subcategory ? ` · ${po.subcategory}` : ""} · Created {new Date(po.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2">
          {po.status === "open" && !(po.invoice_ids?.length || po.invoice_id) && (user?.role === "admin" || user?.id === po.created_by) && (
            <Link to={`${backTo}/${po.id}/edit`} data-testid="edit-po-link">
              <Button variant="outline" className="rounded-sm gap-2">
                <PencilSimple size={16} /> Edit
              </Button>
            </Link>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              className="rounded-sm gap-2"
              onClick={() => setMappingOpen(true)}
              data-testid="edit-mapping-btn"
              title="Change the BOQ item this PO is against — safe on any PO status, does not affect amounts or approval history"
            >
              <PencilSimple size={16} /> Update BOQ Mapping
            </Button>
          )}
          <Button
            variant="outline"
            className="rounded-sm gap-2"
            onClick={handleDownloadPDF}
            disabled={downloading}
            data-testid="download-pdf-btn"
          >
            <FilePdf size={16} /> {downloading ? "Generating…" : "Download PDF"}
          </Button>
          {(po.status === "open" || po.status === "partially_fulfilled") && (
            <Button
              className="rounded-sm bg-primary hover:bg-[#0039CC] gap-2"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-invoice-btn"
            >
              <UploadSimple size={16} />
              {uploading ? "Processing…" : invoices.length > 0 ? "Upload Another Invoice" : "Upload Tax Invoice"}
            </Button>
          )}
          <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={handleFile} className="hidden" data-testid="invoice-file-input" />
        </div>
      </div>

      {/* Metadata strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetaBox icon={<Calendar size={16} />} label="Delivery Date" value={po.delivery_date || "—"} />
        <MetaBox icon={<MapPin size={16} />} label="Site" value={po.site_location || "—"} />
        <MetaBox icon={<Truck size={16} />} label="Category" value={po.category} />
        <MetaBox icon={<FileText size={16} />} label="Raised By" value={po.created_by_name || "—"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Line items */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-neutral-200 rounded-sm">
            <div className="px-6 py-4 border-b border-neutral-200">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Line Items</div>
              <div className="font-display text-xl font-bold mt-0.5">Ordered Materials</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium text-xs uppercase tracking-wider">#</th>
                    <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider">Description</th>
                    <th className="text-right px-3 py-2 font-medium text-xs uppercase tracking-wider">Qty</th>
                    <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider">Unit</th>
                    <th className="text-right px-3 py-2 font-medium text-xs uppercase tracking-wider">Rate</th>
                    <th className="text-right px-3 py-2 font-medium text-xs uppercase tracking-wider">Tax %</th>
                    <th className="text-right px-3 py-2 font-medium text-xs uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {po.line_items.map((l, i) => (
                    <tr key={i} className={`border-t border-neutral-100 ${i % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`po-line-${i}`}>
                      <td className="px-6 py-2.5 text-neutral-500 tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2.5">{l.description}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{l.quantity}</td>
                      <td className="px-3 py-2.5 text-neutral-500">{l.unit}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(l.unit_price)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{l.tax_percent}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(l.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-neutral-50">
                  <tr>
                    <td colSpan={6} className="px-6 py-2 text-right text-neutral-500 text-xs uppercase tracking-wider">Subtotal</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(po.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="px-6 py-2 text-right text-neutral-500 text-xs uppercase tracking-wider">Tax</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(po.tax_amount)}</td>
                  </tr>
                  <tr className="border-t border-neutral-200">
                    <td colSpan={6} className="px-6 py-3 text-right font-display font-bold">Grand Total</td>
                    <td className="px-3 py-3 text-right tabular-nums font-display font-bold text-lg">{fmt(po.total_amount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes & Terms */}
          {(po.notes || po.terms) && (
            <div className="bg-white border border-neutral-200 rounded-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {po.notes && (
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Notes</div>
                  <p className="text-sm text-neutral-700 whitespace-pre-wrap">{po.notes}</p>
                </div>
              )}
              {po.terms && (
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Terms & Conditions</div>
                  <p className="text-sm text-neutral-700 whitespace-pre-wrap">{po.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Validation panel */}
        <div className="lg:col-span-1 space-y-4">
          {invoices.length === 0 ? (
            <div className="bg-white border border-dashed border-neutral-300 rounded-sm p-6 text-center" data-testid="no-invoice-panel">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/8edb9b1d-d169-4c51-9caf-4228cc8b337f/images/de968366efd48c55ff7284c3d62573ca222caf2a683936c63f3b28f840f65706.png"
                alt="No invoice"
                className="h-28 mx-auto mb-4"
              />
              <div className="font-display text-lg font-bold mb-1">No invoice uploaded</div>
              <p className="text-xs text-neutral-500 mb-4 leading-relaxed">
                Upload the vendor's tax invoice. Our AI extracts line items and validates them against this PO in seconds.
              </p>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-sm bg-primary hover:bg-[#0039CC] gap-2 w-full"
                data-testid="upload-invoice-cta"
              >
                <UploadSimple size={16} />
                {uploading ? "Processing…" : "Upload Tax Invoice"}
              </Button>
            </div>
          ) : (
            <ValidationPanel
              invoices={invoices}
              fulfilment={fulfilment}
              poStatus={po.status}
              isAdmin={isAdmin}
              onApprove={(inv) => openApprovalDialog(inv, "approve")}
              onReject={(inv) => openApprovalDialog(inv, "reject")}
              onDelete={handleDeleteInvoice}
              onReverify={handleReverifyInvoice}
              onView={handleViewInvoice}
              reverifyingId={reverifying}
            />
          )}
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={!!approvalDialog} onOpenChange={(o) => !o && setApprovalDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              {approvalDialog?.action === "approve" ? (
                <><ThumbsUp size={20} className="text-[#137333]" /> Approve Invoice</>
              ) : (
                <><ThumbsDown size={20} className="text-[#C5221F]" /> Reject Invoice</>
              )}
            </DialogTitle>
            <DialogDescription>
              {approvalDialog?.action === "approve"
                ? approvalDialog?.invoice?.ai_verdict === "rejected"
                  ? "Override the AI's rejection. This counts the invoice toward PO fulfilment."
                  : "Confirm this invoice. It will count toward cumulative PO fulfilment."
                : "Reject this invoice. It will not count toward PO fulfilment. Notes are required."}
            </DialogDescription>
          </DialogHeader>
          {approvalDialog && (
            <div className="space-y-4">
              <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-neutral-500 uppercase tracking-wider">Invoice</span>
                  <span className="font-mono-num">
                    {approvalDialog.invoice.extracted_data?.invoice_number || approvalDialog.invoice.original_filename}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 uppercase tracking-wider">AI Verdict</span>
                  <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                    approvalDialog.invoice.ai_verdict === "rejected"
                      ? "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]"
                      : "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]"
                  }`}>
                    {approvalDialog.invoice.ai_verdict}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">
                  Notes {approvalDialog.action === "reject" && <span className="text-[#C5221F]">*</span>}
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={approvalDialog.action === "approve" ? "Optional notes for audit trail" : "Reason for rejection (required)"}
                  className="rounded-sm min-h-[110px]"
                  data-testid="approval-notes-input"
                />
              </div>
              <Button
                className={`w-full rounded-sm h-11 ${approvalDialog.action === "approve" ? "bg-[#137333] hover:bg-[#0E5A26]" : "bg-[#C5221F] hover:bg-[#A0181B]"} text-white font-medium gap-2`}
                onClick={submitApproval}
                disabled={submitting}
                data-testid="approval-submit-btn"
              >
                {approvalDialog.action === "approve" ? <ThumbsUp size={16} /> : <ThumbsDown size={16} />}
                {submitting ? "Submitting…" : approvalDialog.action === "approve" ? "Approve Invoice" : "Reject Invoice"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {mappingOpen && (
        <MappingDialog
          po={po}
          onClose={() => setMappingOpen(false)}
          onSaved={async () => { setMappingOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

/* ------------------------- Update BOQ Mapping (admin, any status) ------------------------- */
function MappingDialog({ po, onClose, onSaved }) {
  const [boqId, setBoqId] = useState(po.boq_item_id || null);
  const [saving, setSaving] = useState(false);

  const isMaterial = (po.po_type || "material") === "material";
  const dirty = boqId !== po.boq_item_id;
  const canSave = dirty && (isMaterial ? !!boqId : true) && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = { boq_item_id: boqId ?? "" };
      const { data } = await api.patch(`/pos/${po.id}/mapping`, payload);
      const cascadeMsg = data.invoice_cascade_count ? ` · ${data.invoice_cascade_count} invoice(s) updated` : "";
      toast.success(`BOQ mapping updated${cascadeMsg}`);
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="rounded-sm max-w-xl" data-testid="mapping-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><PencilSimple size={18} /> Update BOQ Mapping</DialogTitle>
          <DialogDescription>
            Change the BOQ item this PO is attributed to. Line items, totals, vendor, dates, approval history and audit trail remain unchanged.
            {po.status !== "open" && <> This PO is <span className="font-medium text-[#B06000]">{po.status?.replace(/_/g, " ")}</span> — the change applies retroactively and any linked invoices will be re-linked automatically.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <BOQItemPicker
            projectId={po.project_id}
            value={boqId}
            onChange={(id) => setBoqId(id)}
            required={isMaterial}
            label="BOQ Item"
            testId="mapping-boq-picker"
          />
          <div className="text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-sm p-2 leading-relaxed">
            <span className="font-medium text-neutral-700">Note</span>: this action is audited (<code className="text-[10px]">po.mapping_updated</code>). All attached invoices are re-pointed to the new BOQ item automatically. Amounts, statuses and approvals are untouched.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={!canSave} className="rounded-sm bg-primary hover:bg-[#0039CC]" data-testid="mapping-save">
            {saving ? "Saving…" : "Save mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaBox({ icon, label, value }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm px-4 py-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-sm font-medium text-neutral-900 truncate">{value}</div>
    </div>
  );
}

function ValidationPanel({ invoices, fulfilment, poStatus, isAdmin, onApprove, onReject, onDelete, onReverify, onView, reverifyingId }) {
  const statusMeta = STATUS_META[poStatus] || STATUS_META.open;
  const progressPct = fulfilment?.progress_percent ?? 0;
  const progressColor =
    poStatus === "accepted" ? "bg-[#137333]" : "bg-primary";

  const approvedCount = fulfilment?.approved_count ?? 0;
  const pendingCount = fulfilment?.pending_count ?? 0;
  const rejectedCount = fulfilment?.rejected_count ?? 0;

  return (
    <>
      {/* Cumulative fulfilment panel */}
      <div className="bg-white border border-neutral-200 rounded-sm p-6" data-testid="fulfilment-panel">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle size={16} weight="fill" className="text-primary" />
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Approved Cumulative Fulfilment</div>
        </div>

        <div className={`p-4 border mb-4 ${statusMeta.color}`}>
          <div className="flex items-center gap-2 mb-1">
            {poStatus === "accepted" && <CheckCircle size={20} weight="fill" />}
            {poStatus === "partially_fulfilled" && <WarningCircle size={20} weight="fill" />}
            {poStatus === "open" && <Receipt size={20} weight="fill" />}
            <div className="font-display font-bold text-lg">{statusMeta.label}</div>
          </div>
          <div className="text-xs leading-relaxed">
            {poStatus === "accepted" && "All approved invoices cover the PO quantity across every line."}
            {poStatus === "partially_fulfilled" && `${(100 - progressPct).toFixed(1)}% pending. Approve more invoices to reach completion.`}
            {poStatus === "open" && (approvedCount === 0 && pendingCount > 0
              ? "Invoices uploaded but none approved yet — open the ledger below to review & approve."
              : "No approved invoices counted yet.")}
          </div>
        </div>

        {/* Approval queue badges */}
        <div className="grid grid-cols-3 gap-2 mb-5 text-center">
          <div className="border border-neutral-200 rounded-sm p-2.5" data-testid="badge-approved">
            <div className="font-display text-xl font-bold text-[#137333] tabular-nums">{approvedCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">Approved</div>
          </div>
          <div className="border border-neutral-200 rounded-sm p-2.5" data-testid="badge-pending">
            <div className="font-display text-xl font-bold text-[#B06000] tabular-nums">{pendingCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">Pending</div>
          </div>
          <div className="border border-neutral-200 rounded-sm p-2.5" data-testid="badge-rejected">
            <div className="font-display text-xl font-bold text-[#C5221F] tabular-nums">{rejectedCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">Rejected</div>
          </div>
        </div>

        {/* Overall progress (approved-only) */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5 text-xs">
            <span className="uppercase tracking-wider text-neutral-500">Approved Progress</span>
            <span className="tabular-nums font-medium text-neutral-900">{progressPct}%</span>
          </div>
          <div className="h-2 bg-neutral-100 overflow-hidden rounded-sm">
            <div className={`h-full ${progressColor} transition-all duration-500`} style={{ width: `${Math.min(progressPct, 100)}%` }} />
          </div>
          <div className="text-[11px] text-neutral-500 mt-1.5 tabular-nums">
            {fulfilment?.total_delivered_quantity ?? 0} / {fulfilment?.total_po_quantity ?? 0} qty · {approvedCount} approved invoice{approvedCount === 1 ? "" : "s"}
          </div>
        </div>

        {/* Per-line progress */}
        {fulfilment?.lines?.length > 0 && (
          <div className="space-y-2.5 mb-2">
            {fulfilment.lines.map((line, i) => {
              const linePct = line.po_quantity > 0 ? Math.min(100, (line.delivered_quantity / line.po_quantity) * 100) : 0;
              const lineColor = line.over_delivered
                ? "bg-[#C5221F]"
                : line.complete
                ? "bg-[#137333]"
                : "bg-primary";
              return (
                <div key={i} data-testid={`fulfilment-line-${i}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-neutral-800 truncate pr-2">{line.description}</span>
                    <span className="tabular-nums text-neutral-600 whitespace-nowrap">
                      {line.delivered_quantity} / {line.po_quantity}
                    </span>
                  </div>
                  <div className="h-1.5 bg-neutral-100 overflow-hidden">
                    <div className={`h-full ${lineColor}`} style={{ width: `${linePct}%`, transition: "width 400ms ease" }} />
                  </div>
                  {line.over_delivered && (
                    <div className="text-[10px] text-[#C5221F] mt-1">Over-delivered by {(line.delivered_quantity - line.po_quantity).toFixed(2)} (admin overrides applied)</div>
                  )}
                  {line.complete && !line.over_delivered && (
                    <div className="text-[10px] text-[#137333] mt-1">Complete</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invoice ledger with summary + approval actions */}
      <div className="bg-white border border-neutral-200 rounded-sm" data-testid="invoice-ledger">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt size={14} className="text-neutral-500" />
            <span className="text-xs uppercase tracking-[0.15em] text-neutral-500">Invoice Ledger</span>
          </div>
          <span className="text-xs text-neutral-500 tabular-nums">{invoices.length}</span>
        </div>
        <div className="divide-y divide-neutral-100">
          {invoices.map((inv, i) => (
            <InvoiceRow
              key={inv.id}
              invoice={inv}
              index={i}
              isAdmin={isAdmin}
              onApprove={() => onApprove(inv)}
              onReject={() => onReject(inv)}
              onDelete={() => onDelete(inv)}
              onReverify={() => onReverify(inv)}
              onView={() => onView(inv)}
              reverifying={reverifyingId === inv.id}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function InvoiceRow({ invoice, index, isAdmin, onApprove, onReject, onDelete, onReverify, onView, reverifying }) {
  const [expanded, setExpanded] = useState(false);
  const aiMeta = invoice.ai_verdict === "rejected"
    ? STATUS_META.rejected
    : STATUS_META.accepted;
  const approvalMeta = APPROVAL_META[invoice.approval_status] || APPROVAL_META.pending_approval;
  const summary = invoice.validation_summary || {};
  const extracted = invoice.extracted_data || {};

  const canApprove =
    invoice.approval_status === "pending_approval" ||
    (invoice.approval_status === "pending_admin_review" && isAdmin);
  const canReject =
    invoice.approval_status === "pending_approval" ||
    (invoice.approval_status === "pending_admin_review" && isAdmin) ||
    (["approved", "admin_approved"].includes(invoice.approval_status) && isAdmin);

  return (
    <div className="p-4 hover:bg-neutral-50 transition-colors" data-testid={`invoice-row-${index}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] tabular-nums text-neutral-400 w-4">#{index + 1}</span>
          <button
            className="font-mono-num text-sm text-neutral-900 truncate hover:text-primary text-left"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`invoice-toggle-${index}`}
          >
            {extracted.invoice_number || invoice.original_filename}
          </button>
        </div>
        <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 border whitespace-nowrap ${approvalMeta.color}`} data-testid={`invoice-approval-badge-${index}`}>
          {approvalMeta.label}
        </span>
      </div>

      <div className="ml-6 flex items-center gap-2 text-[11px] mb-2">
        <span className="text-neutral-500 uppercase tracking-wider">AI:</span>
        <span className={`inline-flex items-center px-1.5 py-0.5 border ${aiMeta.color}`}>
          {invoice.ai_verdict === "rejected" ? "Auto-rejected" : "Auto-accepted"}
        </span>
        <span className="text-neutral-400">·</span>
        <span className="text-neutral-500 tabular-nums">{fmt(extracted.total_amount)}</span>
        <span className="text-neutral-400">·</span>
        <span className="text-neutral-500 tabular-nums">{new Date(invoice.uploaded_at).toLocaleDateString()}</span>
      </div>

      {/* Rules summary chips */}
      <div className="ml-6 flex flex-wrap gap-1.5 mb-2">
        <RuleChip
          icon={<ShieldCheck size={11} />}
          label="Qty"
          passed={summary.rule_quantity?.passed}
          testId={`rule-qty-${index}`}
        />
        <RuleChip
          icon={<ShieldCheck size={11} />}
          label="Price"
          passed={summary.rule_price?.passed}
          testId={`rule-price-${index}`}
        />
        <RuleChip
          icon={<ShieldWarning size={11} />}
          label="Matched lines"
          passed={summary.rule_unmatched?.passed}
          testId={`rule-unmatched-${index}`}
          customText={summary.matched_count > 0 ? `${summary.matched_count + summary.mismatched_count} matched` : null}
        />
      </div>

      {/* Action buttons */}
      <div className="ml-6 flex items-center gap-2 flex-wrap">
        <button
          onClick={onView}
          disabled={!invoice.storage_path}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-neutral-300 text-neutral-700 hover:border-primary hover:text-primary transition-colors rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`view-invoice-btn-${index}`}
          title="Open the uploaded invoice file in a new tab"
        >
          <Eye size={12} /> View
        </button>
        {canApprove && (
          <button
            onClick={onApprove}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-[#137333] text-[#137333] hover:bg-[#137333] hover:text-white transition-colors rounded-sm"
            data-testid={`approve-btn-${index}`}
            title={invoice.approval_status === "pending_admin_review"
              ? "Override AI rejection and manually approve this invoice"
              : "Approve this invoice"}
          >
            <ThumbsUp size={12} />
            {invoice.approval_status === "pending_admin_review" ? "Manual Approve (Override)" : "Approve"}
          </button>
        )}
        {canReject && (
          <button
            onClick={onReject}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-[#C5221F] text-[#C5221F] hover:bg-[#C5221F] hover:text-white transition-colors rounded-sm"
            data-testid={`reject-btn-${index}`}
          >
            <ThumbsDown size={12} /> Reject
          </button>
        )}
        {isAdmin && (
          <button
            onClick={onReverify}
            disabled={reverifying}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-neutral-300 text-neutral-700 hover:border-primary hover:text-primary transition-colors rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`reverify-btn-${index}`}
            title="Re-run AI validation against the current PO (updates stored verdict + fulfilment)"
          >
            <ArrowsClockwise size={12} className={reverifying ? "animate-spin" : ""} />
            {reverifying ? "Re-verifying…" : "Re-verify"}
          </button>
        )}
        {invoice.approval_status === "pending_admin_review" && !isAdmin && (
          <span className="text-[11px] text-[#B06000] flex items-center gap-1">
            <ShieldWarning size={12} /> Awaiting admin
          </span>
        )}
        {isAdmin && (
          <button
            onClick={onDelete}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-neutral-300 text-neutral-500 hover:border-[#C5221F] hover:text-[#C5221F] transition-colors rounded-sm"
            data-testid={`delete-invoice-btn-${index}`}
            title="Delete invoice (admin) — recomputes PO fulfilment"
          >
            <Trash size={12} /> Delete
          </button>
        )}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] text-neutral-500 hover:text-primary ml-auto"
          data-testid={`invoice-expand-${index}`}
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
      </div>

      {/* Expanded summary + approval history */}
      {expanded && (
        <div className="ml-6 mt-3 border-t border-neutral-100 pt-3 space-y-3 text-xs">
          <div className="space-y-1">
            <DetailRow label="Vendor" value={extracted.vendor_name || "—"} />
            <DetailRow label="Invoice Date" value={extracted.invoice_date || "—"} />
            <DetailRow label="Uploaded By" value={invoice.uploaded_by_name || "—"} />
          </div>

          <RulesDetail summary={summary} />

          {(invoice.approved_by_name || invoice.approval_notes) && (
            <div className="border border-neutral-200 bg-neutral-50 rounded-sm p-2.5">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
                <Note size={11} /> Decision Notes
              </div>
              {invoice.approved_by_name && (
                <div className="text-[11px] text-neutral-700">
                  <span className="text-neutral-500">By</span> {invoice.approved_by_name}
                  {invoice.approved_at && <span className="text-neutral-500"> · {new Date(invoice.approved_at).toLocaleString()}</span>}
                </div>
              )}
              {invoice.approval_notes && (
                <div className="text-neutral-700 mt-1 whitespace-pre-wrap">{invoice.approval_notes}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RuleChip({ icon, label, passed, testId, customText }) {
  const ok = passed !== false;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${
        ok ? "border-[#A4D0B3] bg-[#E6F4EA]/60 text-[#137333]" : "border-[#F3B2AC] bg-[#FCE8E6]/60 text-[#C5221F]"
      }`}
      data-testid={testId}
    >
      {ok ? <CheckCircle size={11} weight="fill" /> : <XCircle size={11} weight="fill" />}
      {label}{customText ? ` · ${customText}` : ""}
    </span>
  );
}

function RulesDetail({ summary }) {
  if (!summary || (!summary.rule_quantity?.checks?.length && !summary.rule_unmatched?.lines?.length)) {
    return null;
  }
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">Comparison Summary</div>

      {/* Quantity rule */}
      <RuleSection
        title="Quantity Rule"
        sub="invoice qty ≤ PO qty"
        passed={summary.rule_quantity?.passed}
        items={summary.rule_quantity?.checks || []}
        renderRow={(c, k) => (
          <div key={k} className="flex items-center justify-between text-[11px]">
            <span className="text-neutral-700 truncate pr-2">{c.description}</span>
            <span className={`tabular-nums whitespace-nowrap ${c.passed ? "text-[#137333]" : "text-[#C5221F]"}`}>
              {c.invoice_quantity} ≤ {c.po_quantity} {c.passed ? "✓" : "✗"}
            </span>
          </div>
        )}
      />

      {/* Price rule */}
      <RuleSection
        title="Price Rule"
        sub="invoice unit price = PO unit price"
        passed={summary.rule_price?.passed}
        items={summary.rule_price?.checks || []}
        renderRow={(c, k) => (
          <div key={k} className="flex items-center justify-between text-[11px]">
            <span className="text-neutral-700 truncate pr-2">{c.description}</span>
            <span className={`tabular-nums whitespace-nowrap ${c.passed ? "text-[#137333]" : "text-[#C5221F]"}`}>
              {fmt(c.invoice_unit_price)} / {fmt(c.po_unit_price)} {c.passed ? "✓" : "✗"}
            </span>
          </div>
        )}
      />

      {/* Unmatched lines */}
      {summary.rule_unmatched?.lines?.length > 0 && (
        <RuleSection
          title="Line Match Rule"
          sub="every invoice line matches a PO line"
          passed={false}
          items={summary.rule_unmatched.lines}
          renderRow={(u, k) => (
            <div key={k} className="flex items-center justify-between text-[11px]">
              <span className="text-neutral-700 truncate pr-2">{u.description}</span>
              <span className="tabular-nums text-[#B06000] whitespace-nowrap">Qty {u.quantity}</span>
            </div>
          )}
        />
      )}

      {/* Ancillary charges (auto-filtered, informational) */}
      {summary.rule_ancillary?.lines?.length > 0 && (
        <RuleSection
          title="Ancillary Charges"
          sub="transport / freight / round-off — excluded from line match"
          passed={true}
          items={summary.rule_ancillary.lines}
          renderRow={(u, k) => (
            <div key={k} className="flex items-center justify-between text-[11px]" data-testid={`ancillary-line-${k}`}>
              <span className="text-neutral-700 truncate pr-2">{u.description}</span>
              <span className="tabular-nums text-neutral-600 whitespace-nowrap">Qty {u.quantity}</span>
            </div>
          )}
        />
      )}
    </div>
  );
}

function RuleSection({ title, sub, passed, items, renderRow }) {
  if (!items?.length) return null;
  return (
    <div className={`border rounded-sm p-2.5 ${passed ? "border-[#A4D0B3] bg-[#E6F4EA]/30" : "border-[#F3B2AC] bg-[#FCE8E6]/30"}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <div className={`text-[11px] uppercase tracking-wider font-medium ${passed ? "text-[#137333]" : "text-[#C5221F]"}`}>
            {title}
          </div>
          <div className="text-[10px] text-neutral-500">{sub}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${passed ? "border-[#A4D0B3] text-[#137333]" : "border-[#F3B2AC] text-[#C5221F]"}`}>
          {passed ? "PASS" : "FAIL"}
        </span>
      </div>
      <div className="space-y-0.5">
        {items.map((it, i) => renderRow(it, i))}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-500 text-xs uppercase tracking-wider">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
